import { z } from 'zod';
import {
  LogisticsCalculationEditableContentSchema,
  LogisticsCalculationRateInputSchema,
  LogisticsCargoExpenseSnapshotSchema,
  validatePaymentDivisor,
} from './content.js';
import { LogisticsCalculationPublicViewKindSchema } from './status.js';
import { LogisticsCalculationDetailSchema, LogisticsCalculationListItemSchema } from './wire.js';

export const LogisticsCalculationPublicLinkSchema = z
  .object({ token: z.string().min(1), path: z.string().min(1) })
  .strict();
export type LogisticsCalculationPublicLink = z.infer<typeof LogisticsCalculationPublicLinkSchema>;

// Write-only constraints stay here because the web form reads persisted content
// through the editable schema before a manager can bring it into the new domain.
const LogisticsCalculationWriteContentSchema = LogisticsCalculationEditableContentSchema.extend({
  rubPerUsdRate: LogisticsCalculationRateInputSchema,
});

// The divisor rule is carried here rather than on the editable content schema,
// which must stay a `ZodObject` for the web form's `.strip()` round trip.
export const CreateLogisticsCalculationInputSchema = z
  .object({ content: LogisticsCalculationWriteContentSchema })
  .strict()
  .superRefine(({ content }, ctx) => validatePaymentDivisor(content, ctx, ['content']));
export type CreateLogisticsCalculationInput = z.infer<typeof CreateLogisticsCalculationInputSchema>;

export const PatchLogisticsCalculationInputSchema = z
  .object({ content: LogisticsCalculationWriteContentSchema })
  .strict()
  .superRefine(({ content }, ctx) => validatePaymentDivisor(content, ctx, ['content']));
export type PatchLogisticsCalculationInput = z.infer<typeof PatchLogisticsCalculationInputSchema>;

export const LogisticsCalculationListResponseSchema = z
  .object({ items: z.array(LogisticsCalculationListItemSchema) })
  .strict();
export type LogisticsCalculationListResponse = z.infer<
  typeof LogisticsCalculationListResponseSchema
>;

export const LogisticsCalculationDetailResponseSchema = z
  .object({
    calculation: LogisticsCalculationDetailSchema,
    publicLink: LogisticsCalculationPublicLinkSchema.nullable(),
  })
  .strict();
export type LogisticsCalculationDetailResponse = z.infer<
  typeof LogisticsCalculationDetailResponseSchema
>;

export const ResolvePublicLogisticsCalculationInputSchema = z
  .object({ token: z.string().min(1).max(512) })
  .strict();
export type ResolvePublicLogisticsCalculationInput = z.infer<
  typeof ResolvePublicLogisticsCalculationInputSchema
>;

export const LogisticsCalculationViewItemSchema = z
  .object({
    id: z.string().uuid(),
    kind: LogisticsCalculationPublicViewKindSchema,
    viewedAt: z.string().datetime(),
    ipAddress: z.string(),
    userAgent: z.string().nullable(),
  })
  .strict();
export type LogisticsCalculationViewItem = z.infer<typeof LogisticsCalculationViewItemSchema>;

export const LogisticsCalculationViewsResponseSchema = z
  .object({
    items: z.array(LogisticsCalculationViewItemSchema),
    total: z.number().int().nonnegative(),
  })
  .strict();
export type LogisticsCalculationViewsResponse = z.infer<
  typeof LogisticsCalculationViewsResponseSchema
>;

export const LogisticsCargoCandidatesQuerySchema = z
  .object({
    id: z.string().uuid().optional(),
    q: z.string().trim().max(200).default(''),
    limit: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.id === undefined && value.q.length === 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['q'], message: 'query is too short' });
    }
  });
export type LogisticsCargoCandidatesQuery = z.infer<typeof LogisticsCargoCandidatesQuerySchema>;

export const LogisticsCargoCandidateLineSchema = z
  .object({
    section: z.literal('chinaExpenses'),
    serviceName: z.string(),
    quantity: z.literal(''),
    unit: z.enum(['fixed']),
    rate: z.literal(''),
    currency: z.literal('CNY'),
    convertToUsd: z.literal(false),
    internalCost: z.string(),
  })
  .strict();

export const LogisticsCargoCandidateSchema = z
  .object({
    id: z.string().uuid(),
    article: z.string(),
    name: z.string().nullable(),
    seats: z.string().nullable(),
    weight: z.string().nullable(),
    volume: z.string().nullable(),
    departedAt: z.string().datetime().nullable(),
    arrivedAt: z.string().datetime().nullable(),
    transportType: z.string().nullable(),
    heldBy: z
      .object({ calculationId: z.string().uuid(), publicCode: z.string() })
      .strict()
      .optional(),
    expenseSnapshot: LogisticsCargoExpenseSnapshotSchema.optional(),
    suggestedInternalCostLines: z.array(LogisticsCargoCandidateLineSchema).optional(),
  })
  .strict();
export type LogisticsCargoCandidate = z.infer<typeof LogisticsCargoCandidateSchema>;

export const LogisticsCargoCandidatesResponseSchema = z
  .object({ items: z.array(LogisticsCargoCandidateSchema) })
  .strict();
export type LogisticsCargoCandidatesResponse = z.infer<
  typeof LogisticsCargoCandidatesResponseSchema
>;
